// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useCallback } from 'react';

import { useApiClient } from '@@common';

import {
	CodeBaseAsyncOperationModel,
	CodeBaseStatesModel,
	CodeGenInstructionsModel,
	ContextCodeBaseTypeModel,
	ContextCodeClient,
} from '../../../api/api';

export interface CodeBaseAdvanceResult {
	codeBaseId: string;
	isSuccess: boolean;
	messages: string[];
}

export const useCodeGenController = (contextId: string) => {
	const client = useApiClient(ContextCodeClient);

	const instructDomainLogicChanges = useCallback(
		async (instruction: string) => {
			try {
				const instruct: CodeGenInstructionsModel = { instruction };
				const response = await client.instructCodeGenChangesToDomainLogic(contextId, instruct);
				return response.messages;
			} catch (error) {
				console.error('Failed to instruct domain logic changes:', error);
				throw error;
			}
		},
		[client, contextId],
	);

	const instructApplicationOperationLogicChanges = useCallback(
		async (operationId: string, instruction: string) => {
			try {
				const instruct: CodeGenInstructionsModel = { instruction };
				const response = await client.instructCodeGenChangesToApplicationOperation(contextId, operationId, instruct);
				return response.messages;
			} catch (error) {
				console.error('Failed to instruct application operation logic changes:', error);
				throw error;
			}
		},
		[client, contextId],
	);

	const instructApplicationEventHandlerLogicChanges = useCallback(
		async (eventHandlerId: string, instruction: string) => {
			try {
				const instruct: CodeGenInstructionsModel = { instruction };
				const response = await client.instructCodeGenChangesToApplicationEventHandler(contextId, eventHandlerId, instruct);
				return response.messages;
			} catch (error) {
				console.error('Failed to instruct application event handler logic changes:', error);
				throw error;
			}
		},
		[client, contextId],
	);

	const packageDomainLogic = useCallback(async () => {
		try {
			console.log('Packaging domain logic for context:', contextId);
			return await client.packageCode(contextId, ContextCodeBaseTypeModel.Domain);
		} catch (error) {
			console.error('Failed to package domain logic:', error);
			throw error;
		}
	}, [client, contextId]);

	const packageApplicationLogic = useCallback(async () => {
		try {
			console.log('Packaging application logic for context:', contextId);
			return await client.packageCode(contextId, ContextCodeBaseTypeModel.Application);
		} catch (error) {
			console.error('Failed to package application logic:', error);
			throw error;
		}
	}, [client, contextId]);

	const syncSchemaToCodeContractFiles = useCallback(
		async (operationId: string) => {
			try {
				console.log('Syncing schemas to code contract files for context:', contextId);
				return await client.syncOperationContract(contextId, operationId);
			} catch (error) {
				console.error('Failed to sync schemas to code contract files:', error);
				throw error;
			}
		},
		[client, contextId],
	);

	const deployApplicationLogic = useCallback(async () => {
		try {
			console.log('Deploying application logic for context:', contextId);
			return await client.deployCode(contextId, ContextCodeBaseTypeModel.Application);
		} catch (error) {
			console.error('Failed to deploy application logic:', error);
			throw error;
		}
	}, [client, contextId]);

	const deployDomainLogic = useCallback(async () => {
		try {
			console.log('Deploying domain logic for context:', contextId);
			return await client.deployCode(contextId, ContextCodeBaseTypeModel.Domain);
		} catch (error) {
			console.error('Failed to deploy domain logic:', error);
			throw error;
		}
	}, [client, contextId]);

	const getCodeOperation = useCallback(
		async (operationId: string) => {
			try {
				return await client.getCodeOperation(contextId, operationId);
			} catch (error) {
				console.error('Failed to get code operation status:', error);
				throw error;
			}
		},
		[client, contextId],
	);

	const trackCodeOperation = useCallback(
		async (operation?: CodeBaseAsyncOperationModel) => {
			if (!operation?.operationId) {
				return operation;
			}

			const startedAt = Date.now();
			let current = operation;

			while (!isTerminalOperation(current)) {
				if (Date.now() - startedAt > CODE_OPERATION_POLL_TIMEOUT_MS) {
					return {
						...current,
						status: CODE_OPERATION_FAILED_STATUS,
						messages: [`${current.operation} did not complete within the expected time.`],
					};
				}

				await delay(CODE_OPERATION_POLL_INTERVAL_MS);
				current = await getCodeOperation(operation.operationId);
			}

			return current;
		},
		[getCodeOperation],
	);

	const advanceCodeBase = useCallback(
		async (codeBaseId: string, next: CodeBaseStatesModel, current: CodeBaseStatesModel) => {
			var result: CodeBaseAdvanceResult | undefined = undefined;

			try {
				if (current === CodeBaseStatesModel.Changed && next === CodeBaseStatesModel.Packaged) {
					const queued = await packageApplicationLogic();
					const completed = await trackCodeOperation(queued);
					result = createOperationResult(codeBaseId, completed);
				}
				if ((current === CodeBaseStatesModel.Packaged || current === CodeBaseStatesModel.Deployed) && next === CodeBaseStatesModel.Deployed) {
					const queued = await deployApplicationLogic();
					const completed = await trackCodeOperation(queued);
					result = createOperationResult(codeBaseId, completed);
				}

				return result;
			} catch (error) {
				console.error('Error advancing code base state:', error);
				// Return error result instead of swallowing the error
				const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during deployment.';
				return {
					isSuccess: false,
					messages: [errorMessage],
					codeBaseId: codeBaseId,
				};
			}
		},
		[packageApplicationLogic, deployApplicationLogic, trackCodeOperation],
	);

	const getContextDomainCodeFiles = useCallback(
		async (contextId: string) => {
			try {
				const codeFiles = await client.getContextCode(contextId, ContextCodeBaseTypeModel.Domain);
				console.log('Loaded Context Domain Code Files', contextId, codeFiles);
				return codeFiles;
			} catch (error) {
				console.error('Failed to get context code files:', error);
			}
		},
		[client],
	);

	const getCodeBaseDomainInfo = useCallback(
		async (contextId: string) => {
			try {
				console.log('Getting code base domain model for context:', contextId);
				var codeBaseInfo = await client.getCodeBaseInformation(contextId, ContextCodeBaseTypeModel.Domain);
				return codeBaseInfo;
			} catch (error) {
				console.error('Failed to get domain code base information:', error);
				throw error;
			}
		},
		[client],
	);

	const getDomainCodeMetaInfo = useCallback(
		async (contextId: string) => {
			try {
				console.log('Getting code metadata domain model for context:', contextId);
				var codeMetadataInfo = await client.getContextDomainMeta(contextId);
				return codeMetadataInfo;
			} catch (error) {
				console.error('Failed to get domain code metadata information:', error);
				throw error;
			}
		},
		[client],
	);

	const getCodeBaseApplicationInfo = useCallback(
		async (contextId: string) => {
			try {
				console.log('Getting code base application model for context:', contextId);
				var codeBaseInfo = await client.getCodeBaseInformation(contextId, ContextCodeBaseTypeModel.Application);
				return codeBaseInfo;
			} catch (error) {
				console.error('Failed to get application code base information:', error);
				throw error;
			}
		},
		[client],
	);

	const getCodeBaseTestInfo = useCallback(
		async (contextId: string) => {
			try {
				console.log('Getting code base test model for context:', contextId);
				var codeBaseInfo = await client.getCodeBaseInformation(contextId, ContextCodeBaseTypeModel.Tests);
				return codeBaseInfo;
			} catch (error) {
				console.error('Failed to get test code base information:', error);
				throw error;
			}
		},
		[client],
	);

	const getContextApplicationCodeFiles = useCallback(
		async (contextId: string) => {
			try {
				const codeFiles = await client.getContextCode(contextId, ContextCodeBaseTypeModel.Application);
				console.log('Loaded Context Application Code Files', contextId, codeFiles);
				return codeFiles;
			} catch (error) {
				console.error('Failed to get context code files:', error);
			}
		},
		[client],
	);

	const getContextTestCodeFiles = useCallback(
		async (contextId: string) => {
			try {
				const codeFiles = await client.getContextCode(contextId, ContextCodeBaseTypeModel.Tests);
				console.log('Loaded Context Test Code Files', contextId, codeFiles);
				return codeFiles;
			} catch (error) {
				console.error('Failed to get context test code files:', error);
			}
		},
		[client],
	);

	const runContextUnitTests = useCallback(async () => {
		try {
			console.log('Running generated unit tests for context:', contextId);
			return await client.runUnitTests(contextId);
		} catch (error) {
			console.error('Failed to run generated unit tests:', error);
			throw error;
		}
	}, [client, contextId]);

	return {
		instructDomainLogicChanges,
		instructApplicationOperationLogicChanges,
		instructApplicationEventHandlerLogicChanges,
		advanceCodeBase,
		packageDomainLogic,
		packageApplicationLogic,
		getContextDomainCodeFiles,
		getCodeBaseDomainInfo,
		getCodeBaseApplicationInfo,
		getCodeBaseTestInfo,
		getContextApplicationCodeFiles,
		getContextTestCodeFiles,
		runContextUnitTests,
		deployApplicationLogic,
		deployDomainLogic,
		getCodeOperation,
		trackCodeOperation,
		syncSchemaToCodeContractFiles,
		getDomainCodeMetaInfo,
	};
};

const CODE_OPERATION_FAILED_STATUS = 'Failed';
const CODE_OPERATION_POLL_INTERVAL_MS = 1500;
const CODE_OPERATION_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const TERMINAL_OPERATION_STATUSES = new Set(['succeeded', 'failed']);

const delay = (timeoutMs: number) => new Promise((resolve) => window.setTimeout(resolve, timeoutMs));

const isTerminalOperation = (operation?: CodeBaseAsyncOperationModel) =>
	operation?.status ? TERMINAL_OPERATION_STATUSES.has(operation.status.toLowerCase()) : false;

const createOperationResult = (codeBaseId: string, operation?: CodeBaseAsyncOperationModel): CodeBaseAdvanceResult => ({
	isSuccess: !operation || operation.status.toLowerCase() !== CODE_OPERATION_FAILED_STATUS.toLowerCase(),
	messages: operation?.messages?.length ? operation.messages : operation ? [`${operation.operation} ${operation.status.toLowerCase()}.`] : [],
	codeBaseId: operation?.codeBaseId ?? codeBaseId,
});
